import { useState, useEffect, useRef, useCallback } from 'react';
import { Location } from './types';

interface UseLocationCarouselReturn {
    currentSlide: number;
    totalSlides: number;
    nextSlide: () => void;
    prevSlide: () => void;
    goToSlide: (index: number) => void;
    containerRef: React.RefObject<HTMLDivElement>;
    containerWidth: number;
    cardsPerSlide: number;
    isAutoPlaying: boolean;
    setIsAutoPlaying: (playing: boolean) => void;
    handleMouseEnter: () => void;
    handleMouseLeave: () => void;
    handleTouchStart: (e: React.TouchEvent) => void;
    handleTouchMove: (e: React.TouchEvent) => void;
    handleTouchEnd: () => void;
}

export function useLocationCarousel(locations: Location[]): UseLocationCarouselReturn {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const touchEndRef = useRef<{ x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [cardsPerSlide, setCardsPerSlide] = useState(3);

    useEffect(() => {
        // Intentional state sync on a prop transition, guarded so it cannot loop.
        // react-doctor-disable-next-line no-adjust-state-on-prop-change
        setCurrentSlide(0);
    }, [locations]);

    useEffect(() => {
        const updateMeasurements = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
                const cssValue = getComputedStyle(containerRef.current)
                    .getPropertyValue('--cards-per-slide')
                    .trim();
                const parsed = parseInt(cssValue, 10);
                if (!isNaN(parsed) && parsed > 0) {
                    setCardsPerSlide(parsed);
                }
            }
        };

        updateMeasurements();
        window.addEventListener('resize', updateMeasurements);
        return () => window.removeEventListener('resize', updateMeasurements);
    }, []);

    const totalSlides = Math.ceil(locations.length / cardsPerSlide);

    useEffect(() => {
        if (currentSlide >= totalSlides) {
            // Intentional state sync on a prop transition, guarded so it cannot loop.
            // react-doctor-disable-next-line no-adjust-state-on-prop-change
            setCurrentSlide(0);
        }
    }, [totalSlides, currentSlide]);

    const goToSlide = useCallback(
        (slideIndex: number) => {
            const newSlideIndex = (slideIndex + totalSlides) % totalSlides;
            setCurrentSlide(newSlideIndex);
        },
        [totalSlides]
    );

    const nextSlide = useCallback(() => {
        goToSlide(currentSlide + 1);
    }, [currentSlide, goToSlide]);

    const prevSlide = useCallback(() => {
        goToSlide(currentSlide - 1);
    }, [currentSlide, goToSlide]);

    useEffect(() => {
        if (isAutoPlaying) {
            autoPlayRef.current = setInterval(nextSlide, 5000);
        }
        return () => {
            if (autoPlayRef.current) {
                clearInterval(autoPlayRef.current);
            }
        };
    }, [isAutoPlaying, nextSlide]);

    const handleMouseEnter = () => setIsAutoPlaying(false);
    const handleMouseLeave = () => setIsAutoPlaying(true);

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        touchEndRef.current = null;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!touchStartRef.current) return;
        touchEndRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };

    const handleTouchEnd = () => {
        if (!touchStartRef.current || !touchEndRef.current) return;
        const deltaX = touchEndRef.current.x - touchStartRef.current.x;
        const deltaY = touchEndRef.current.y - touchStartRef.current.y;

        if (Math.abs(deltaX) > 30 && Math.abs(deltaX) > Math.abs(deltaY)) {
            if (deltaX < 0) {
                nextSlide();
            } else {
                prevSlide();
            }
        }
        touchStartRef.current = null;
    };

    return {
        currentSlide,
        totalSlides,
        nextSlide,
        prevSlide,
        goToSlide,
        containerRef,
        containerWidth,
        cardsPerSlide,
        isAutoPlaying,
        setIsAutoPlaying,
        handleMouseEnter,
        handleMouseLeave,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd
    };
}

